/**
 * FocusBanner — immersive "In Focus" banner using AppStatusBanner.
 *
 * Desktop: horizontal scroll carousel with numbered tasks, navigation arrows.
 * Mobile: vertical stack with max 40vh before scrolling.
 *
 * Each task is prefixed with a large semi-transparent index number.
 * Single task is centered; multiple tasks grow to the right and scroll.
 */

import { memo, useCallback, useEffect, useRef, useState } from "react"
import { useLiveQueryImmediate } from "@/db/hooks"
import { getFocusedTasks, uncompleteTask, cleanupStaleFocusEntries } from "@/db/operations"
import { usePendingCompletions } from "@/features/tasks/stores/pendingCompletions"
import { routeContainerStatus } from "@/features/project-header/projectStatusRequest"
import { ObjectStatus } from "@/db/schema"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { useFocusStore } from "./focusStore"
import { AppStatusBanner } from "@/app/components/AppStatusBanner"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { useRevealTask } from "@/features/search/useRevealTask"
import { useBreakpoint } from "@/shared/hooks/useBreakpoint"
import ChevronLeftIcon from "@/shared/icons/totodoicons/stroke/chevron-left.svg?react"
import ChevronRightIcon from "@/shared/icons/totodoicons/stroke/chevron-right.svg?react"

export type FocusBannerStyle = "prominent" | "subtle" | "off"

export const FocusBanner = memo(function FocusBanner() {
  const { value: bannerStyle } = useSetting<FocusBannerStyle>("#focus-banner-style", "prominent")
  const { isMobile } = useBreakpoint()

  const [focusedTasks] = useLiveQueryImmediate(
    () => getFocusedTasks(),
    ["focusedTasks"],
    undefined,
  )

  // Sync focused tasks to global store (provides focusedIds set + localStorage cache)
  const setFromLiveData = useFocusStore((s) => s.setFromLiveData)
  useEffect(() => {
    if (focusedTasks) {
      setFromLiveData(focusedTasks)
    }
  }, [focusedTasks, setFromLiveData])

  // Cleanup stale focus entries outside liveQuery context
  useEffect(() => {
    if (focusedTasks) {
      void cleanupStaleFocusEntries()
    }
  }, [focusedTasks])

  const handleComplete = useCallback((id: string) => {
    usePendingCompletions.getState().add(id, "complete")
  }, [])

  const handleUncomplete = useCallback((id: string) => {
    void routeContainerStatus(id, ObjectStatus.Unfinished).then((handled) => {
      if (!handled) return uncompleteTask(id)
    })
  }, [])

  const revealTask = useRevealTask()
  const handleOpen = useCallback((id: string) => {
    void revealTask(id)
  }, [revealTask])

  // Use cached data for instant render before live query resolves
  const cachedTasks = useFocusStore((s) => s.cachedTasks)
  const hasCachedFocus = !focusedTasks && cachedTasks.length > 0
  const isVisible = bannerStyle !== "off" && (
    (!!focusedTasks && focusedTasks.length > 0) || hasCachedFocus
  )

  // Horizontal scroll state (desktop)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [showLeft, setShowLeft] = useState(false)
  const [showRight, setShowRight] = useState(false)

  const checkScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setShowLeft(el.scrollLeft > 4)
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    const frame = requestAnimationFrame(checkScroll)
    const observer = new ResizeObserver(checkScroll)
    if (scrollRef.current) observer.observe(scrollRef.current)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [checkScroll, focusedTasks])

  useEffect(() => {
    const root = document.documentElement
    if (isVisible) {
      root.classList.add("focus-banner-active")
      root.style.setProperty("--topbar-status-inset", "0px")
    } else {
      root.classList.remove("focus-banner-active")
      root.style.removeProperty("--topbar-status-inset")
    }
    return () => {
      root.classList.remove("focus-banner-active")
      root.style.removeProperty("--topbar-status-inset")
    }
  }, [isVisible])

  // Publish the banner's real rendered height as --focus-banner-height so
  // fixed UI outside the layout tree (e.g. FloatingDoneButton) can anchor
  // below it instead of overlapping. The banner's height is dynamic (one
  // row per focused task, up to 40vh then internal scroll), so a
  // ResizeObserver on its own root tracks it live as rows are added/removed.
  const bannerRootRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = bannerRootRef.current
    const root = document.documentElement
    if (!el) {
      root.style.setProperty("--focus-banner-height", "0px")
      return
    }
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height ?? el.offsetHeight
      root.style.setProperty("--focus-banner-height", `${height}px`)
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.style.setProperty("--focus-banner-height", "0px")
    }
  }, [isVisible])

  if (!isVisible) return null

  const isSubtle = bannerStyle === "subtle"
  // Use live data if available, otherwise cached placeholder tasks
  const displayTasks = (focusedTasks ?? cachedTasks) as NonNullable<typeof focusedTasks>
  const count = displayTasks.length

  // Desktop: horizontal scroll carousel
  // Mobile: vertical stack
  return (
    <AppStatusBanner
      ref={bannerRootRef}
      color={isSubtle ? "primary" : "destructive"}
      visible
      noPadding
    >
      {isMobile ? (
        /* ── Mobile: vertical stack, max 40vh then scroll ── */
        <div className="focus-banner-items flex flex-col gap-0 overflow-y-auto px-2" style={{ maxHeight: "40vh" }}>
          {displayTasks.map((task, i) => (
            <div
              key={task.id}
              className={`relative flex items-center ${
                i < count - 1 ? "border-b border-white/15" : ""
              }`}
            >
              <span data-focus-number className="pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center text-white/[0.15]" style={{ fontSize: "42px", fontWeight: 900, lineHeight: 1, fontVariantNumeric: "tabular-nums", width: "32px", textAlign: "center" }}>
                {i + 1}
              </span>
              <div className="min-w-0 flex-1" style={{ paddingLeft: "32px" }}>
                <TaskItem
                  task={task}
                  onComplete={handleComplete}
                  onUncomplete={handleUncomplete}
                  onOpen={handleOpen}
                  isFirstInGroup
                  isLastInGroup
                  showDateChip
                  compact
                  hideFocusIcon
                  forceViewId="today"
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Desktop: horizontal scroll carousel ── */
        <div className="relative">
          <div
            ref={scrollRef}
            className="overflow-x-auto overflow-y-hidden scrollbar-none"
            style={{ padding: "5px 20px" }}
            onScroll={checkScroll}
          >
            <div className="focus-banner-items flex w-fit items-center gap-4" style={{ margin: "0 auto" }}>
            {displayTasks.map((task, i) => (
              <div
                key={task.id}
                className="relative flex shrink-0 items-center"
                style={{ maxWidth: "420px", minWidth: "280px" }}
              >
                <span data-focus-number className="pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center text-white/[0.15]" style={{ fontSize: "clamp(50px, 7vw, 90px)", fontWeight: 900, lineHeight: 1, fontVariantNumeric: "tabular-nums", width: "clamp(40px, 5.5vw, 68px)", textAlign: "center" }}>
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1" style={{ paddingLeft: "clamp(36px, 5vw, 64px)" }}>
                  <TaskItem
                    task={task}
                    onComplete={handleComplete}
                    onUncomplete={handleUncomplete}
                    onOpen={handleOpen}
                    disabled
                    isFirstInGroup
                    isLastInGroup
                    showDateChip
                    compact
                    hideFocusIcon
                    forceViewId="today"
                  />
                </div>
              </div>
            ))}
            </div>
          </div>

          {/* Navigation arrows */}
          {showLeft && (
            <button
              type="button"
              aria-label="Scroll left"
              onClick={() => {
                const el = scrollRef.current
                if (el) el.scrollBy({ left: -el.clientWidth * 0.6, behavior: "smooth" })
              }}
              className="absolute inset-y-0 left-0 z-10 flex w-12 cursor-pointer items-center bg-gradient-to-r from-black/30 to-transparent"
            >
              <ChevronLeftIcon className="size-5 text-white/70" />
            </button>
          )}
          {showRight && (
            <button
              type="button"
              aria-label="Scroll right"
              onClick={() => {
                const el = scrollRef.current
                if (el) el.scrollBy({ left: el.clientWidth * 0.6, behavior: "smooth" })
              }}
              className="absolute inset-y-0 right-0 z-10 flex w-12 cursor-pointer items-center justify-end bg-gradient-to-l from-black/30 to-transparent"
            >
              <ChevronRightIcon className="size-5 text-white/70" />
            </button>
          )}
        </div>
      )}
    </AppStatusBanner>
  )
})


